import { AxiosResponse } from "axios";
import { api, CustomAxiosRequestConfig, ENDPOINTS } from "@app/api";
import { InspectionsType } from "@app/types";
import {
  DATE_TIME_FORMAT_YEAR_AND_MONTH_1,
  DATE_TIME_FORMAT_YEAR_AND_MONTH_2,
} from "@app/constants";
import { format } from "date-fns";

export const apiInspectionFormTemplates = (
  groupId: string,
  machineId: string,
): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.inspectionFormTemplates(groupId, machineId));
};

export const apiInspectionFormTemplatesAll = (
  groupId: string,
  machineId: string,
): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.inspectionFormTemplatesAll(groupId, machineId));
};

export const apiGetBlankInspectionFormTemplate = (
  groupId: string,
  machineId: string,
): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.getBlankInspectionFormTemplate(groupId, machineId));
};

export const apiInspectionForms = (
  groupId: string,
  machineId: string,
): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.inspectionForms(groupId, machineId));
};

export const apiInspectionFormsWeb = (
  groupId: string,
  machineId: string,
): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.inspectionFormsWeb(groupId, machineId), {
    skipGlobalLoader: true,
  } as CustomAxiosRequestConfig);
};

export const apiInspectionTemplatesDetail = (
  groupId: string,
  machineId: string,
  inspectionFormId: string,
): Promise<AxiosResponse> => {
  return api.get(
    ENDPOINTS.inspectionFormDetail(groupId, machineId, inspectionFormId),
  );
};

export const apiPutCustomInspectionForm = (
  groupId: string,
  machineId: string,
  inspectionFormId: string,
  payload: InspectionsType,
): Promise<AxiosResponse> => {
  return api.put(
    ENDPOINTS.customInspectionFormDetail(groupId, machineId, inspectionFormId),
    { ...payload },
  );
};

export const apiCustomInspectionTemplatesDetail = (
  groupId: string,
  machineId: string,
  customInspectionFormId: string,
): Promise<AxiosResponse> => {
  return api.get(
    ENDPOINTS.customInspectionFormDetail(
      groupId,
      machineId,
      customInspectionFormId,
    ),
  );
};

export const apiPostInspectionForms = (
  groupId: string,
  machineId: string,
  payload: any,
): Promise<AxiosResponse> => {
  return api.post(ENDPOINTS.inspectionForm(groupId, machineId), {
    ...payload,
  });
};

export const apiGetInspectionForms = (
  groupId: string,
  machineId: string,
): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.inspectionForm(groupId, machineId));
};

export const apiUploadStore = (file: File): Promise<AxiosResponse> => {
  return api.put(ENDPOINTS.uploadStore, file);
};

export const apiGetInspections = (
  groupId: string,
  machineId: string,
  params: {
    monthYear: string;
    customInspectionFormId: string;
  },
): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.inspections(groupId, machineId), {
    params,
    skipGlobalLoader: true,
  } as CustomAxiosRequestConfig);
};

export const apiGetInspectionsPDF = (
  groupId: string,
  machineId: string,
  params: {
    monthYear: string;
    customInspectionFormId: string;
    pdfHeaderRemarks?: string;
    pdfFooterRemarks?: string;
  },
): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.inspectionPdf(groupId, machineId), {
    params,
  });
};

export const getInspectionFormTemplates = (
  groupId: string,
  machineId: string,
) => {
  return apiInspectionFormTemplates(groupId, machineId).then((db) => {
    const { data } = db.data;
    return data;
  });
};

export const getInspectionFormTemplatesAll = (
  groupId: string,
  machineId: string,
) => {
  return apiInspectionFormTemplatesAll(groupId, machineId).then((db) => {
    const { data } = db.data;
    return data;
  });
};

export const getInspectionForms = (groupId: string, machineId: string) => {
  return apiInspectionForms(groupId, machineId).then((db) => {
    const { data } = db.data;
    return data;
  });
};

export const getInspectionFormsWeb = (groupId: string, machineId: string) => {
  return apiInspectionFormsWeb(groupId, machineId).then((db) => {
    const { data } = db.data;
    return data;
  });
};

export const getInspectionFormTemplatesDetail = (
  groupId: string,
  machineId: string,
  inspectionFormId: string,
) => {
  return apiInspectionTemplatesDetail(
    groupId,
    machineId,
    inspectionFormId,
  ).then((db) => {
    const { data } = db.data;
    return data;
  });
};

export const getCustomInspectionFormTemplates = (
  groupId: string,
  machineId: string,
  customInspectionFormId: string,
) => {
  return apiCustomInspectionTemplatesDetail(
    groupId,
    machineId,
    customInspectionFormId,
  ).then((db) => {
    const { data } = db.data;
    return data;
  });
};

export const getCustomInspectionFormTemplatess = (
  groupId: string,
  machineId: string,
  customInspectionFormId: string,
) => {
  return apiCustomInspectionTemplatesDetails(
    groupId,
    machineId,
    customInspectionFormId,
  ).then((db) => {
    const { data } = db.data;
    return data;
  });
};

export const apiCustomInspectionTemplatesDetails = (
  groupId: string,
  machineId: string,
  customInspectionFormId: string,
): Promise<AxiosResponse> => {
  return api.get(
    ENDPOINTS.customInspectionFormDetails(
      groupId,
      machineId,
      customInspectionFormId,
    ),
  );
};

export const apiGetCustomInspectionFormWeb = (
  groupId: string,
  machineId: string,
  customInspectionFormId: string,
  params: { dateTime: string; timezone: string },
): Promise<AxiosResponse> => {
  return api.get(
    ENDPOINTS.customInspectionFormDetails(
      groupId,
      machineId,
      customInspectionFormId,
    ),
    {
      params,
      skipGlobalLoader: true,
    } as CustomAxiosRequestConfig,
  );
};

export const getCustomInspectionFormWeb = (
  groupId: string,
  machineId: string,
  customInspectionFormId: string,
  params: { dateTime: string; timezone: string },
) => {
  return apiGetCustomInspectionFormWeb(
    groupId,
    machineId,
    customInspectionFormId,
    params,
  ).then((response) => response?.data?.data ?? null);
};

export const postInspectionFormTemplates = (
  groupId: string,
  machineId: string,
  payload: InspectionsType,
) => {
  const payloadToSend = {
    ...payload,
    inspectionFormType:
      payload.inspectionFormType ?? payload.type ?? "TEMPLATE",
  } as InspectionsType;

  return apiPostInspectionForms(groupId, machineId, payloadToSend);
};

export const getInspectionForm = (groupId: string, machineId: string) => {
  return apiGetInspectionForms(groupId, machineId).then((db) => {
    const { data } = db.data;
    return data;
  });
};

export const apiUploadUrls = (payload: any): Promise<AxiosResponse> => {
  return api.post(ENDPOINTS.uploadUrls, {
    ...payload,
  });
};

export const postUploadUrls = (payload: { type: string; fileName: string }) => {
  return apiUploadUrls(payload)
    .then((db) => {
      const { data } = db.data;
      return data;
    })
    .catch(() => {
      throw new Error();
    });
};

export const putCustomInspectionForm = (
  groupId: string,
  machineId: string,
  inspectionFormId: string,
  payload: InspectionsType,
) => {
  return apiPutCustomInspectionForm(
    groupId,
    machineId,
    inspectionFormId,
    payload,
  );
};

export const putUploadStore = (file: File) => {
  return apiUploadStore(file)
    .then((db) => {
      const { data } = db.data;
      return data;
    })
    .catch(() => {
      throw new Error();
    });
};

export const getInspections = (
  groupId: string,
  machineId: string,
  params: {
    monthYear: string;
    customInspectionFormId: string;
    utc?: string;
  },
) => {
  return apiGetInspections(groupId, machineId, params).then((db) => {
    const { data } = db.data;
    return data;
  });
};

const arrayBufferToFile = (arrayBuffer: any, fileName: string) => {
  const blob = new Blob([new Uint8Array(arrayBuffer)], {
    type: "application/pdf",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");

  a.style.display = "none";
  a.href = url;
  a.download = fileName;

  document.body.appendChild(a);
  a.click();
  const isFirefox = navigator.userAgent.toLowerCase().includes("firefox");
  if (!isFirefox) {
    window.open(url, "_blank");
  }

  setTimeout(() => {
    window.URL.revokeObjectURL(url);
  }, 1000);
};

export const getInspectionsPDF = (
  groupId: string,
  machineId: string,
  params: {
    monthYear: Date;
    customInspectionFormId: string;
    utc?: string;
    pdfHeaderRemarks?: string;
    pdfFooterRemarks?: string;
  },
) => {
  return apiGetInspectionsPDF(groupId, machineId, {
    ...params,
    monthYear: format(
      new Date(params?.monthYear),
      DATE_TIME_FORMAT_YEAR_AND_MONTH_1,
    ),
  }).then((db) => {
    const { data } = db.data.data;
    const arrayBuffer = new Uint8Array(data).buffer;
    arrayBufferToFile(
      arrayBuffer,
      `${format(
        new Date(params?.monthYear),
        DATE_TIME_FORMAT_YEAR_AND_MONTH_2,
      )}_InspectionResult.pdf`,
    );
  });
};

export const apiGetMachinesExcel = (
  groupId: string,
): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.exportExcel(groupId));
};

export const getMachinesExcel = (groupId: string) => {
  return apiGetMachinesExcel(groupId)
    .then((response: { data: { data: any } }) => {
      const { data } = response.data;

      const byteCharacters = atob(data);
      const byteNumbers = new Array(byteCharacters.length)
        .fill(0)
        .map((_, i) => byteCharacters.charCodeAt(i));
      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      const now = new Date();
      const formattedDate = now.toISOString().split("T")[0];
      const formattedTime = now.toTimeString().split(" ")[0].replace(/:/g, "-");

      link.href = url;
      link.download = `machines-export-${formattedDate}-(${formattedTime}).xlsx`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    })
    .catch((error: any) => {
      console.error("Error downloading Excel file:", error);
      throw new Error("Failed to download the Excel file.");
    });
};

export const apiGetInspectionPDF = (
  groupId: string,
  machineId: string,
  inspectionId: string,
  timeZone: string,
  headerRemarks: string,
  footerRemarks: string,
): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.inspectionsPdf(groupId, machineId, inspectionId), {
    params: { timeZone, headerRemarks, footerRemarks },
  });
};

export const getInspectionPDF = (
  groupId: string,
  machineId: string,
  inspectionId: string,
  timeZone: string,
  headerRemarks: string,
  footerRemarks: string,
  machineName: string,
  timeFormat: string,
  reportName: string,
  reportedAt: string,
  customInspectionFormName: string,
) => {
  return apiGetInspectionPDF(
    groupId,
    machineId,
    inspectionId,
    timeZone,
    headerRemarks,
    footerRemarks,
  )
    .then((response) => {
      const { data } = response.data;
      const byteCharacters = atob(data);
      const byteNumbers = new Array(byteCharacters.length)
        .fill(0)
        .map((_, i) => byteCharacters.charCodeAt(i));
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = buildReportFileName(
        reportName,
        machineName,
        timeFormat,
        reportedAt,
        customInspectionFormName,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      const isFirefox = navigator.userAgent.toLowerCase().includes("firefox");
      if (!isFirefox) {
        window.open(url, "_blank");
      }

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    })
    .catch((error) => {
      console.error("Error downloading inspection PDF:", error);
      throw new Error("Failed to download the inspection PDF.");
    });
};

export const getMonthlyInspectionPDF = (
  groupId: string,
  machineId: string,
  customInspectionFormId: string,
  timeZoneUtc: string,
  timeZone: string,
  headerRemarks: string,
  footerRemarks: string,
  monthYear: Date,
  reportedAt: Date,
  timeFormat: string,
  customInspectionFormName: string,
  machineName: string,
) => {
  return apiGetMonthlyInspectionPDF(
    groupId,
    machineId,
    customInspectionFormId,
    timeZoneUtc,
    timeZone,
    headerRemarks,
    footerRemarks,
    format(new Date(monthYear), DATE_TIME_FORMAT_YEAR_AND_MONTH_1),
  )
    .then((response) => {
      const { data } = response.data;
      const byteCharacters = atob(data);
      const byteNumbers = new Array(byteCharacters.length)
        .fill(0)
        .map((_, i) => byteCharacters.charCodeAt(i));
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = buildReportFileNameMonthlyInspection(
        reportedAt,
        timeFormat,
        customInspectionFormName,
        machineName,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      const isFirefox = navigator.userAgent.toLowerCase().includes("firefox");
      if (!isFirefox) {
        window.open(url, "_blank");
      }

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    })
    .catch((error) => {
      console.error("Error downloading inspection PDF:", error);
      throw new Error("Failed to download the inspection PDF.");
    });
};

export const apiGetMonthlyInspectionPDF = (
  groupId: string,
  machineId: string,
  customInspectionFormId: string,
  timeZoneUtc: string,
  timeZone: string,
  headerRemarks: string,
  footerRemarks: string,
  monthYear: string,
): Promise<AxiosResponse> => {
  return api.get(
    ENDPOINTS.monthlyInspectionsPdf(groupId, machineId, customInspectionFormId),
    {
      params: {
        timeZoneUtc,
        timeZone,
        headerRemarks,
        footerRemarks,
        monthYear,
      },
    },
  );
};

export const apiGetReportMachineNameInfo = (
  reportId: string,
): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.machineNameInfo(reportId));
};

export const apiGetMonthlyInspectionInfo = (
  groupId: string,
  machineId: string,
  customInspectionFormId: string,
): Promise<AxiosResponse> => {
  return api.get(
    ENDPOINTS.monthlyInspectionInfo(groupId, machineId, customInspectionFormId),
  );
};

export const apiGetReportMachineNameInfoMaintenance = (
  groupId: string,
  machineId: string,
  reportId: string,
): Promise<AxiosResponse> => {
  return api.get(
    ENDPOINTS.machineNameInfoMaintenance(groupId, machineId, reportId),
  );
};

export const apiGetMachineReoportsPDF = (
  groupId: string,
  machineId: string,
  machineReportId: string,
  timeZone: string,
  headerRemarks: string,
  footerRemarks: string,
): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.machinesPdf(groupId, machineId, machineReportId), {
    params: { timeZone, headerRemarks, footerRemarks },
  });
};

export const getYYYYMMDD = (timeFormat: string, reportedAt: string) => {
  const d = new Date(reportedAt);

  const yyyy = d.getFullYear().toString();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");

  switch (timeFormat) {
    case "MM/dd/yyyy":
      return `${mm}${dd}${yyyy}`;

    case "dd/MM/yyyy":
      return `${dd}${mm}${yyyy}`;

    case "yyyy/MM/dd":
    default:
      return `${yyyy}${mm}${dd}`;
  }
};

export const getYYYYMM = (timeFormat: string, reportedAt: string) => {
  const d = new Date(reportedAt);

  const yyyy = d.getFullYear().toString();
  const mm = String(d.getMonth() + 1).padStart(2, "0");

  switch (timeFormat) {
    case "MM/dd/yyyy":
      return `${mm}${yyyy}`;

    case "dd/MM/yyyy":
      return `${mm}${yyyy}`;

    case "yyyy/MM/dd":
    default:
      return `${yyyy}${mm}`;
  }
};

export const buildReportFileName = (
  reportTitle: string,
  machineName: string,
  timeFormat: string,
  reportedAt: string,
  customInspectionFormName?: string,
) => {
  const datePart = getYYYYMMDD(timeFormat, reportedAt);

  let name = `${datePart}_${customInspectionFormName}_${machineName}`;

  if (name.length > 50) {
    name = name.substring(0, 49) + "~";
  }

  return name + ".pdf";
};

export const buildReportFileNameMonthlyInspection = (
  reportedAt: Date,
  timeFormat: string,
  customInspectionFormName: string,
  machineName: string,
) => {
  const datePart = getYYYYMM(timeFormat, reportedAt.toISOString());

  let name = `${datePart}_${customInspectionFormName}_${machineName}`;

  if (name.length > 50) {
    name = name.substring(0, 49) + "~";
  }

  return name + ".pdf";
};

export const buildReportFileNameMaintenance = (
  reportTitle: string,
  machineName: string,
  timeFormat: string,
  workAt: string,
  customInspectionFormName?: string,
) => {
  const datePart = getYYYYMMDD(timeFormat, workAt);

  let name = `${datePart}_${reportTitle}_${machineName}`;

  if (name.length > 50) {
    name = name.substring(0, 49) + "~";
  }

  return name + ".pdf";
};

export const getMachinePDF = (
  groupId: string,
  machineId: string,
  machineReportId: string,
  timeZone: string,
  headerRemarks: string,
  footerRemarks: string,
  machineName: string,
  timeFormat: string,
  reportName: string,
  reportedAt: string,
  reportTitle: string,
) => {
  return apiGetMachineReoportsPDF(
    groupId,
    machineId,
    machineReportId,
    timeZone,
    headerRemarks,
    footerRemarks,
  )
    .then((response) => {
      const { data } = response.data;
      const byteCharacters = atob(data);
      const byteNumbers = new Array(byteCharacters.length)
        .fill(0)
        .map((_, i) => byteCharacters.charCodeAt(i));
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = buildReportFileName(
        reportName,
        machineName,
        timeFormat,
        reportedAt,
        reportTitle,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      const isFirefox = navigator.userAgent.toLowerCase().includes("firefox");
      if (!isFirefox) {
        window.open(url, "_blank");
      }

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    })
    .catch((error) => {
      console.error("Error downloading machine report PDF:", error);
      throw new Error("Failed to download the machine report PDF.");
    });
};

export const apiGetMachineMaintenanceReoportsPDF = (
  groupId: string,
  machineId: string,
  machineReportId: string,
  timeZone: string,
  pdfMaintHeaderRemarks: string,
  pdfMaintFooterRemarks: string,
): Promise<AxiosResponse> => {
  return api.get(
    ENDPOINTS.machinesMaintenancePdf(groupId, machineId, machineReportId),
    {
      params: {
        timeZone,
        headerRemarks: pdfMaintHeaderRemarks,
        footerRemarks: pdfMaintFooterRemarks,
      },
    },
  );
};

export const getMachineMaintenancePDF = (
  groupId: string,
  machineId: string,
  machineReportId: string,
  timeZone: string,
  pdfMaintHeaderRemarks: string,
  pdfMaintFooterRemarks: string,
  machineName: string,
  timeFormat: string,
  reportTitle: string,
  workAt: string,
) => {
  return apiGetMachineMaintenanceReoportsPDF(
    groupId,
    machineId,
    machineReportId,
    timeZone,
    pdfMaintHeaderRemarks,
    pdfMaintFooterRemarks,
  )
    .then((response) => {
      const { data } = response.data;
      const byteCharacters = atob(data);
      const byteNumbers = new Array(byteCharacters.length)
        .fill(0)
        .map((_, i) => byteCharacters.charCodeAt(i));
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = buildReportFileNameMaintenance(
        reportTitle,
        machineName,
        timeFormat,
        workAt,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      const isFirefox = navigator.userAgent.toLowerCase().includes("firefox");
      if (!isFirefox) {
        window.open(url, "_blank");
      }

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    })
    .catch((error) => {
      console.error("Error downloading machine report PDF:", error);
      throw new Error("Failed to download the machine report PDF.");
    });
};

export const apiGetInspectionItems = (
  groupId: string,
  machineId: string,
  customInspectionFormId: string,
): Promise<AxiosResponse> => {
  return api.get(
    ENDPOINTS.customInspectionFormDetail(
      groupId,
      machineId,
      customInspectionFormId,
    ),
  );
};

export const getInspectionItems = (
  groupId: string,
  machineId: string,
  customInspectionFormId: string,
) => {
  return apiGetInspectionItems(groupId, machineId, customInspectionFormId).then(
    (response) => {
      const { data } = response.data;
      return data.inspectionItems.map((item: any) => ({
        id: item.inspectionItemId,
        name: item.name,
        description: item.description,
        inspectionItemMedias: item.inspectionItemMedias || [],
        isRequired: item.isRequired,
        position: item.position,
        itemCode: item.itemCode,
      }));
    },
  );
};

export const apiGetInspectionItemsNew = (
  customInspectionFormId: string,
): Promise<AxiosResponse> => {
  return api.get(
    ENDPOINTS.customInspectionFormDetailNew(customInspectionFormId),
  );
};

export const getInspectionItemsNew = (customInspectionFormId: string) => {
  return apiGetInspectionItemsNew(customInspectionFormId).then((response) => {
    const { data } = response.data;
    return data.inspectionItems.map((item: any) => ({
      id: item.inspectionItemId,
      name: item.name,
      description: item.description,
      inspectionItemMedias: item.inspectionItemMedias || [],
      isRequired: item.isRequired,
      position: item.position,
      itemCode: item.itemCode,
      resultType: item.resultType,
      options: (item.options ?? []).map((option: any) => ({
        customInspectionItemsOptionsId: option.customInspectionItemsOptionsId,
        name: option.name,
        position: option.position,
      })),
    }));
  });
};

export const apiGetInspectionItemsFormStatus = (
  groupId: string,
  machineId: string,
  customInspectionFormId: string,
): Promise<AxiosResponse> => {
  return api.get(
    ENDPOINTS.customInspectionFormDetail(
      groupId,
      machineId,
      customInspectionFormId,
    ),
  );
};

export const getInspectionItemsFormStatus = (
  groupId: string,
  machineId: string,
  customInspectionFormId: string,
) => {
  return apiGetInspectionItemsFormStatus(
    groupId,
    machineId,
    customInspectionFormId,
  ).then((response) => {
    const fullData = response.data;
    return fullData;
  });
};

export const apiDeleteInspection = (
  groupId: string,
  machineId: string,
  inspectionId: string,
): Promise<AxiosResponse> => {
  return api.delete(
    ENDPOINTS.inspectionDetail(groupId, machineId, inspectionId),
  );
};

export const deleteInspection = (
  groupId: string,
  machineId: string,
  inspectionId: string,
) => {
  return apiDeleteInspection(groupId, machineId, inspectionId)
    .then((response) => {
      return {
        deletedInspectionId: inspectionId,
      };
    })
    .catch((error) => {
      console.error(
        "Error deleting inspection:",
        error.response?.data || error.message,
      );
      throw new Error("Failed to delete inspection");
    });
};

export const apiCreateInspection = (
  groupId: string,
  machineId: string,
  payload: any,
): Promise<AxiosResponse> => {
  return api.post(ENDPOINTS.createInspection(groupId, machineId), payload);
};

export const apiGetLatestInspection = (
  groupId: string,
  machineId: string,
  customInspectionFormId: string,
): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.latestInspection(groupId, machineId), {
    params: { customInspectionFormId },
    skipGlobalLoader: true,
  } as CustomAxiosRequestConfig);
};

export const getLatestInspection = (
  groupId: string,
  machineId: string,
  customInspectionFormId: string,
) => {
  return apiGetLatestInspection(groupId, machineId, customInspectionFormId).then(
    (response) => response?.data?.data ?? null,
  );
};

export const apiGetInspectionDetail = (
  groupId: string,
  machineId: string,
  inspectionId: string,
): Promise<AxiosResponse> => {
  return api.get(
    ENDPOINTS.getInspectionDetail(groupId, machineId, inspectionId),
  );
};

export const apiGetInspectionItemDetail = (
  groupId: string,
  machineId: string,
  inspectionId: string,
  inspectionItemId: string,
): Promise<AxiosResponse> => {
  return api.get(
    ENDPOINTS.getInspectionItemDetail(
      groupId,
      machineId,
      inspectionId,
      inspectionItemId,
    ),
  );
};

export const apiUpdateInspection = (
  groupId: string,
  machineId: string,
  inspectionId: string,
  payload: any,
): Promise<AxiosResponse> => {
  return api.put(
    ENDPOINTS.updateInspection(groupId, machineId, inspectionId),
    payload,
  );
};

export const apiUpdateInspectionFiltered = (
  groupId: string,
  machineId: string,
  inspectionId: string,
  payload: any,
): Promise<AxiosResponse> => {
  return api.put(
    ENDPOINTS.updateInspectionFiltered(groupId, machineId, inspectionId),
    payload,
  );
};

export const apiUpdateInspectionItem = (
  groupId: string,
  machineId: string,
  inspectionId: string,
  inspectionResultId: string,
  payload: any,
): Promise<AxiosResponse> => {
  const endpoint = ENDPOINTS.updateInspectionItem(
    groupId,
    machineId,
    inspectionId,
    inspectionResultId,
  );
  return api.put(endpoint, payload);
};

export const apiGetInspectionAt = (
  groupId: string,
  machineId: string,
  inspectionId: string,
): Promise<AxiosResponse> => {
  const endpoint = ENDPOINTS.getInspectionAt(groupId, machineId, inspectionId);
  return api.get(endpoint);
};

export const apiUpdateInspectionAt = (
  groupId: string,
  machineId: string,
  inspectionId: string,
  payload: { inspectionAt: string },
): Promise<AxiosResponse> => {
  return api.put(
    ENDPOINTS.updateInspectionAt(groupId, machineId, inspectionId),
    payload,
  );
};

export type BulkReportPdfItem = {
  reportId: string;
  type: string;
  reportedAt: string;
};

export const apiPostBulkReportsPdf = (
  groupId: string,
  machineId: string,
  reports: BulkReportPdfItem[],
  timeZone: string,
  zip: boolean,
  isMedia: boolean,
  headerRemarks: string,
  footerRemarks: string,
): Promise<AxiosResponse> => {
  return api.post(
    ENDPOINTS.bulkReportsPdf(groupId, machineId),
    { reports },
    {
      params: {
        timeZone,
        zip,
        naIsMedia: isMedia,
        headerRemarks,
        footerRemarks,
      },
    },
  );
};

const downloadBase64File = (
  base64Data: string,
  mimeType: string,
  fileName: string,
  openInNewTab = false,
) => {
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length)
    .fill(0)
    .map((_, i) => byteCharacters.charCodeAt(i));
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  const isFirefox = navigator.userAgent.toLowerCase().includes("firefox");
  if (openInNewTab && !isFirefox) {
    window.open(url, "_blank");
  }

  setTimeout(() => {
    window.URL.revokeObjectURL(url);
  }, 1000);
};

export const getBulkReportsPdf = (
  groupId: string,
  machineId: string,
  reports: BulkReportPdfItem[],
  timeZone: string,
  zip: boolean,
  fileName: string,
  isMedia = true,
  headerRemarks = "",
  footerRemarks = "",
) => {
  return apiPostBulkReportsPdf(
    groupId,
    machineId,
    reports,
    timeZone,
    zip,
    isMedia,
    headerRemarks,
    footerRemarks,
  )
    .then((response) => {
      const { data, meta } = response.data;
      const fileType = meta?.fileType === "zip" ? "zip" : "pdf";
      const mimeType =
        fileType === "zip" ? "application/zip" : "application/pdf";

      downloadBase64File(data, mimeType, fileName, fileType === "pdf");

      return meta;
    })
    .catch((error) => {
      console.error("Error downloading bulk report PDF:", error);
      throw new Error("Failed to download bulk report PDF.");
    });
};
